import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Loader2,
  UserCheck,
  LogIn,
  Smartphone,
  Download
} from 'lucide-react';
import Logo from '@/components/Logo';
import { apiClient } from '@/services/api/apiClient';
import Cookies from 'js-cookie';

const WaiterInviteAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [inviteData, setInviteData] = useState<any>(null);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!Cookies.get('auth_token');
  
  // Detect if mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (inviteCode) {
      validateInvite();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [inviteCode]);

  const validateInvite = async () => {
    setValidating(true);
    try {
      // Public endpoint - no auth required
      const response = await apiClient.get('/waiter/auth/validate-invitation', {
        params: { code: inviteCode }
      });
      
      setInviteData(response.data);
      setLoading(false);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
      setLoading(false);
    } finally {
      setValidating(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!isAuthenticated) {
      // Store invite code and redirect to login
      localStorage.setItem('pending_invite_code', inviteCode || '');
      navigate('/restaurant-login');
      return;
    }

    setAccepting(true);
    setError('');

    try {
      // Get auth token
      const authToken = Cookies.get('auth_token');
      if (!authToken) {
        toast({
          title: "Authentication Required",
          description: "Please log in to continue",
          variant: "destructive",
        });
        navigate('/restaurant-login');
        return;
      }
      
      // Get user data from token
      let userData = {
        auth_provider: 'oauth',
        auth_id: authToken,
        email: '',  // Will be set from invite data
        name: ''     // Will be set from invite data
      };
      
      // Override with actual user data if available from inviteData
      if (inviteData?.waiter_email) {
        userData.email = inviteData.waiter_email;
      }
      if (inviteData?.waiter_name) {
        userData.name = inviteData.waiter_name;
      }

      await apiClient.post('/waiter/auth/accept-invitation', {
        invitation_code: inviteCode,
        ...userData
      });
      
      setAccepted(true);
      
      // In production, this would redirect to the mobile app
      // For now, show success message
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  // Check for pending invite after login
  useEffect(() => {
    const pendingCode = localStorage.getItem('pending_invite_code');
    if (pendingCode && isAuthenticated && !inviteCode) {
      localStorage.removeItem('pending_invite_code');
      navigate(`/waiter/invite?code=${pendingCode}`);
    }
  }, [isAuthenticated, inviteCode]);

  if (loading || validating) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-white animate-spin mx-auto mb-4" />
          <p className="text-white">Validating invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center p-4">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <Logo className="text-white h-12 mx-auto mb-4" />
          </div>

          {error ? (
            // Error State
            <div className="text-center">
              <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Invalid Invitation</h1>
              <p className="text-white/60 mb-6">{error}</p>
              <button
                onClick={() => navigate('/restaurant-login')}
                className="bg-white/20 text-white rounded-lg px-6 py-3 font-medium hover:bg-white/30 transition-colors"
              >
                Go to Login
              </button>
            </div>
          ) : accepted ? (
            // Success State - Simple for now
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Invitation Accepted!</h1>
              <p className="text-white/60 mb-6">
                You've successfully joined {inviteData.restaurant_name}
              </p>
              <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-4">
                <p className="text-blue-300 text-sm">
                  The Dine AI Waiter App will be available soon. 
                  We'll notify you when it's ready for download.
                </p>
              </div>
            </div>
          ) : (
            // Valid Invitation State
            <div className="text-center">
              <UserCheck className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Staff Invitation</h1>
              
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <p className="text-white/80 mb-2">You've been invited to join:</p>
                <p className="text-xl font-semibold text-white">{inviteData?.restaurant_name}</p>
                {inviteData?.sender_name && (
                  <p className="text-white/60 text-sm mt-2">Invited by {inviteData.sender_name}</p>
                )}
              </div>

              {!isAuthenticated ? (
                // Not logged in
                <div className="space-y-4">
                  <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-3">
                    <AlertCircle className="h-4 w-4 text-blue-400 inline mr-2" />
                    <span className="text-blue-300 text-sm">
                      Please sign in to accept this invitation
                    </span>
                  </div>
                  
                  <button
                    onClick={handleAcceptInvite}
                    className="w-full bg-[#FDB913] text-blue-900 rounded-lg py-3 px-4 font-medium hover:bg-[#FDB913]/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <LogIn className="h-4 w-4" />
                    Sign In to Accept
                  </button>
                </div>
              ) : (
                // Logged in - can accept
                <div className="space-y-4">
                  <button
                    onClick={handleAcceptInvite}
                    disabled={accepting}
                    className="w-full bg-[#FDB913] text-blue-900 rounded-lg py-3 px-4 font-medium hover:bg-[#FDB913]/90 transition-colors disabled:opacity-50"
                  >
                    {accepting ? 'Accepting...' : 'Accept Invitation'}
                  </button>
                  
                  <button
                    onClick={() => navigate('/restaurant-dashboard')}
                    className="w-full bg-white/20 text-white rounded-lg py-3 px-4 font-medium hover:bg-white/30 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default WaiterInviteAccept;